// /festive-events/:id/menu — Festive Menu Editor (PAGES.md §3.87)
//
// Create and manage special menus for festive events. Separate from regular menu —
// includes sweets combos, family packs, party trays, catering items. Set bulk/qty
// pricing, limited-time combos, link to production planning.
// RESTAURANT vertical only — MANAGER+ (level 30). Feature flag: ENABLE_FESTIVE_MODULE.
// Traceability: FR-FEST-003, FR-FEST-004, FR-FEST-006

import { getSubunitMultiplier } from '@bbpos/utils';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Badge,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  DataTable,
  Tabs,
  Toggle,
  Modal,
  Input,
  Select,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import { festiveService } from '@/services/festive.service';
import type { Festival, FestiveMenuItem, AddFestiveMenuItemDto } from '@/services/festive.service';
import { productService } from '@/services/product.service';
import { recipeService } from '@/services/recipe.service';
import { useAuthStore } from '@/stores/use-auth-store';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { GLASS, GLASS_INTERACTIVE, cn } from '@/constants/design-tokens';
import { formatPaise } from '@/utils/currency';
import { ApiError } from '@/lib/api-client';
import {
  ROLE_LEVELS,
  MENU_CATEGORIES as CATEGORIES,
  MENU_CATEGORY_OPTIONS as CATEGORY_OPTIONS,
} from './constants';
import { PlusIcon, MenuIcon, TrashIcon, DownloadIcon } from './icons';

// ─── Mobile Menu Card ───────────────────────────────────────────────────────

function MenuItemMobileCard({ item, onToggle }: { item: FestiveMenuItem; onToggle: () => void }) {
  const currency = useTenantCurrency();
  return (
    <div className={cn(GLASS_INTERACTIVE, 'p-4 animate-slide-up')}>
      <div className="flex items-start gap-3">
        {item.image_url && (
          <img src={item.image_url} alt={item.product_name} className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="font-bold text-neutral-900 dark:text-neutral-100 truncate">{item.product_name}</p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{item.category}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-neutral-500 dark:text-neutral-400 line-through font-mono tabular-nums">
              {formatPaise(item.regular_price, currency)}
            </span>
            <span className="text-sm font-bold text-violet-700 dark:text-violet-400 font-mono tabular-nums">
              {formatPaise(item.festive_price, currency)}
            </span>
          </div>
        </div>
        <Toggle checked={item.is_available} onChange={onToggle} />
      </div>
      {item.bulk_pricing_tiers.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {item.bulk_pricing_tiers.map((tier, i) => (
            <Badge key={i} variant="draft" size="sm">
              {tier.min_quantity}+: {tier.discount_percentage}% off
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

function FestiveMenuEditorContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const canEdit = roleLevel >= 30;

  // ── State ───────────────────────────────────────────────────────────────
  const [festival, setFestival] = useState<Festival | null>(null);
  const [items, setItems] = useState<FestiveMenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');

  // ── Add item modal ──────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [formProductId, setFormProductId] = useState('');
  const [formCategory, setFormCategory] = useState('SWEETS');
  const [formFestivePrice, setFormFestivePrice] = useState('');
  const [formLinkedRecipeId, setFormLinkedRecipeId] = useState('');
  const [formTiers, setFormTiers] = useState<Array<{ min: string; max: string; discount: string }>>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ── Product & recipe pickers ──────────────────────────────────────────────
  const [productOptions, setProductOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [recipeOptions, setRecipeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [productPriceMap, setProductPriceMap] = useState<Record<string, number>>({});

  // ── Fetch ───────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [event, menuItems] = await Promise.all([
        festiveService.getFestival(id),
        festiveService.getFestiveMenu(id),
      ]);
      setFestival(event);
      setItems(menuItems);
      document.title = `Festive Menu — ${event.name} — Orio360 Admin`;
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 404) {
        toast({ title: 'Festival event not found', variant: 'error' });
        navigate('/festive-events');
      } else {
        toast({ title: err instanceof ApiError ? err.message : 'Failed to load menu.', variant: 'error' });
      }
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtered items ──────────────────────────────────────────────────────
  const filteredItems = useMemo(
    () => activeCategory === 'all' ? items : items.filter((i) => i.category === activeCategory),
    [items, activeCategory],
  );

  // ── Toggle availability ─────────────────────────────────────────────────
  const toggleAvailability = useCallback(async (item: FestiveMenuItem) => {
    if (!id) return;
    try {
      await festiveService.updateFestiveMenuItem(id, item.id, { is_available: !item.is_available });
      setItems((prev) => prev.map((i) => (i.id === item.id ? { ...i, is_available: !i.is_available } : i)));
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to update.', variant: 'error' });
    }
  }, [id, toast]);

  // ── Delete item ─────────────────────────────────────────────────────────
  const deleteItem = useCallback(async (itemId: string) => {
    if (!id) return;
    try {
      await festiveService.deleteFestiveMenuItem(id, itemId);
      setItems((prev) => prev.filter((i) => i.id !== itemId));
      toast({ title: 'Item removed', variant: 'success' });
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to remove.', variant: 'error' });
    }
  }, [id, toast]);

  // ── Add item ────────────────────────────────────────────────────────────
  // Load selectable products and recipes for the picker dropdowns.
  const loadPickerData = useCallback(async () => {
    try {
      const [prodRes, recipeRes] = await Promise.all([
        productService.list({ limit: 200, is_active: 'true', sort: 'name', order: 'asc' }),
        recipeService.listRecipes({ limit: 200, is_active: true }),
      ]);
      const priceMap: Record<string, number> = {};
      setProductOptions(
        prodRes.data.map((p) => {
          const price = (p as unknown as { price?: number }).price ?? 0;
          priceMap[p.id] = price;
          return { value: p.id, label: p.sku ? `${p.name} (${p.sku})` : p.name };
        }),
      );
      setProductPriceMap(priceMap);
      setRecipeOptions(
        recipeRes.data.map((r) => ({
          value: r.id,
          label: r.name && r.name !== r.product_name ? `${r.name} — ${r.product_name}` : r.product_name,
        })),
      );
    } catch (err) {
      toast({ title: err instanceof ApiError ? err.message : 'Failed to load products/recipes.', variant: 'error' });
    }
  }, [toast]);

  const openAddModal = useCallback(() => {
    setFormProductId('');
    setFormCategory('SWEETS');
    setFormFestivePrice('');
    setFormLinkedRecipeId('');
    setFormTiers([]);
    setFormError(null);
    setModalOpen(true);
    loadPickerData();
  }, [loadPickerData]);

  const addTier = useCallback(() => {
    setFormTiers((prev) => [...prev, { min: '', max: '', discount: '' }]);
  }, []);

  const updateTier = useCallback((index: number, field: 'min' | 'max' | 'discount', value: string) => {
    setFormTiers((prev) => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  }, []);

  const removeTier = useCallback((index: number) => {
    setFormTiers((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddItem = useCallback(async () => {
    if (!id || !formProductId || !formFestivePrice) {
      setFormError('Product and festive price are required');
      return;
    }
    setIsSaving(true);
    setFormError(null);
    try {
      const dto: AddFestiveMenuItemDto = {
        product_id: formProductId,
        category: formCategory,
        festive_price: Math.round(parseFloat(formFestivePrice) * getSubunitMultiplier(currency)), // major → minor
        linked_recipe_id: formLinkedRecipeId || undefined,
        bulk_pricing_tiers: formTiers
          .filter((t) => t.min && t.max && t.discount)
          .map((t) => ({
            min_quantity: parseInt(t.min, 10),
            max_quantity: parseInt(t.max, 10),
            discount_percentage: parseFloat(t.discount),
          })),
        is_available: true,
      };
      await festiveService.addFestiveMenuItem(id, dto);
      toast({ title: 'Item added to festive menu', variant: 'success' });
      setModalOpen(false);
      fetchData();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Failed to add item');
    } finally {
      setIsSaving(false);
    }
  }, [id, formProductId, formCategory, formFestivePrice, formTiers, fetchData, toast]);

  // ── Keyboard shortcuts ──────────────────────────────────────────────────
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
      if (e.key === 'n' && !modalOpen && canEdit) { e.preventDefault(); openAddModal(); }
      if (e.key === 's' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        toast({ title: 'Menu saved', variant: 'success' });
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalOpen, canEdit, openAddModal, toast]);

  // ── Table columns ─────────────────────────────────────────────────────
  const columns: ColumnDef<FestiveMenuItem>[] = useMemo(() => [
    {
      key: 'product_name', header: 'Item',
      render: (_v: unknown, row: FestiveMenuItem) => (
        <div className="flex items-center gap-3">
          {row.image_url && (
            <img src={row.image_url} alt="" className="w-8 h-8 rounded-lg object-cover" />
          )}
          <span className="font-bold text-neutral-900 dark:text-neutral-100">{row.product_name}</span>
        </div>
      ),
    },
    {
      key: 'category', header: 'Category',
      render: (_v: unknown, row: FestiveMenuItem) => (
        <Badge variant="draft" size="sm">{row.category.replace('_', ' ')}</Badge>
      ),
    },
    {
      key: 'regular_price', header: 'Regular', numeric: true,
      render: (_v: unknown, row: FestiveMenuItem) => (
        <span className="font-mono tabular-nums text-neutral-500 dark:text-neutral-400 line-through">
          {formatPaise(row.regular_price, currency)}
        </span>
      ),
    },
    {
      key: 'festive_price', header: 'Festive Price', numeric: true,
      render: (_v: unknown, row: FestiveMenuItem) => (
        <span className="font-mono tabular-nums font-bold text-violet-700 dark:text-violet-400">
          {formatPaise(row.festive_price, currency)}
        </span>
      ),
    },
    {
      key: 'bulk_pricing_tiers', header: 'Bulk Pricing',
      render: (_v: unknown, row: FestiveMenuItem) =>
        row.bulk_pricing_tiers.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {row.bulk_pricing_tiers.map((t, i) => (
              <Badge key={i} variant="draft" size="sm">{t.min_quantity}+: {t.discount_percentage}%</Badge>
            ))}
          </div>
        ) : (
          <span className="text-xs text-neutral-400">—</span>
        ),
    },
    {
      key: 'is_available', header: 'Available',
      render: (_v: unknown, row: FestiveMenuItem) => (
        <Toggle checked={row.is_available} onChange={() => toggleAvailability(row)} />
      ),
    },
    {
      key: 'actions', header: '',
      render: (_v: unknown, row: FestiveMenuItem) => (
        <Button variant="danger" size="sm" onClick={() => deleteItem(row.id)}>
          <TrashIcon className="h-3.5 w-3.5" />
        </Button>
      ),
    },
  ], [toggleAvailability, deleteItem, currency]);

  if (isLoading) {
    return (
      <PageContainer
        title="Loading..."
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Festive Events', href: '/festive-events' },
          { label: '...' },
          { label: 'Menu' },
        ]}
      >
        <div className="animate-fade-in space-y-6">
          <div className={cn(GLASS, 'p-6')}>
            <Skeleton variant="text" width="40%" height={28} />
            <div className="mt-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} variant="rect" width="100%" height={48} />
              ))}
            </div>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={`Festive Menu — ${festival?.name ?? ''}`}
      subtitle="Manage special menu items, bulk pricing, and availability"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Festive Events', href: '/festive-events' },
        { label: festival?.name ?? '', href: `/festive-events/${id}` },
        { label: 'Menu' },
      ]}
      actions={
        <div className="flex items-center gap-3">
          <div className="hidden sm:flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => toast({ title: 'Import from main menu coming soon', variant: 'info' })}>
              <DownloadIcon /><span className="ml-1.5">Import from Main Menu</span>
            </Button>
            <Button variant="primary" size="sm" onClick={openAddModal} disabled={!canEdit}>
              <PlusIcon /><span className="ml-1.5">Add Item</span>
            </Button>
          </div>
        </div>
      }
    >
      <div className="animate-fade-in">
        {/* ═══ Category Tabs ═══ */}
        <Tabs
          items={CATEGORIES.map((c) => ({ id: c.key, label: c.label }))}
          activeId={activeCategory}
          onChange={setActiveCategory}
        />

        <div className="mt-6">
          {filteredItems.length === 0 ? (
            <EmptyState
              variant="page"
              icon={<MenuIcon className="h-14 w-14" />}
              title="No festive menu items"
              description="Add items to your festive menu"
              action={canEdit ? { label: 'Add Item', onClick: openAddModal } : undefined}
            />
          ) : (
            <>
              {/* Desktop */}
              <div className={cn(GLASS, 'overflow-hidden hidden sm:block mb-6')}>
                <div className="px-5 py-4">
                  <h3 className="text-sm font-bold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Menu Items ({filteredItems.length})
                  </h3>
                </div>
                <DataTable<FestiveMenuItem> columns={columns} data={filteredItems} />
              </div>

              {/* Mobile */}
              <div className="block sm:hidden space-y-3 mb-6">
                {filteredItems.map((item) => (
                  <MenuItemMobileCard
                    key={item.id}
                    item={item}
                    onToggle={() => toggleAvailability(item)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══ Mobile FAB ═══ */}
      {canEdit && (
        <button
          type="button"
          onClick={openAddModal}
          className="fixed bottom-6 right-6 sm:hidden z-40 w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-pink-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center active:scale-95"
          aria-label="Add Item"
        >
          <PlusIcon />
        </button>
      )}

      {/* ═══ Add Item Modal ═══ */}
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Add Festive Menu Item" variant="wide">
        <div className="space-y-4 p-1 max-h-[70vh] overflow-y-auto">
          {formError && (
            <div className="text-xs text-danger-600 bg-danger-50 dark:bg-danger-900/20 px-3 py-2 rounded-lg">
              {formError}
            </div>
          )}

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Product *</label>
            <Select
              variant="searchable"
              options={productOptions}
              value={formProductId}
              onChange={(val) => {
                const pid = typeof val === 'string' ? val : val[0] ?? '';
                setFormProductId(pid);
                setFormError(null);
                // Prefill festive price from the product's price if not yet set.
                if (pid && !formFestivePrice && productPriceMap[pid] != null) {
                  setFormFestivePrice((productPriceMap[pid] / getSubunitMultiplier(currency)).toString());
                }
              }}
              placeholder="Search and select a product"
              clearable
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Category *</label>
              <Select
                options={CATEGORY_OPTIONS}
                value={formCategory}
                onChange={(val) => setFormCategory(typeof val === 'string' ? val : val[0] ?? 'SWEETS')}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Festive Price ({getCurrencySymbol(currency)}) *</label>
              <Input
                type="number"
                value={formFestivePrice}
                onChange={(val) => { setFormFestivePrice(val); setFormError(null); }}
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1 block">Linked Recipe</label>
            <Select
              variant="searchable"
              options={recipeOptions}
              value={formLinkedRecipeId}
              onChange={(val) => setFormLinkedRecipeId(typeof val === 'string' ? val : val[0] ?? '')}
              placeholder="Search and select a recipe (optional)"
              clearable
            />
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              Links this item to a recipe for production planning.
            </p>
          </div>

          {/* Bulk pricing tiers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-medium text-neutral-500 dark:text-neutral-400">Bulk Pricing Tiers</label>
              <Button variant="secondary" size="sm" onClick={addTier}>
                <PlusIcon /><span className="ml-1">Add Tier</span>
              </Button>
            </div>
            {formTiers.map((tier, i) => (
              <div key={i} className="flex items-center gap-2 mb-2">
                <Input type="number" placeholder="Min Qty" value={tier.min} onChange={(val) => updateTier(i, 'min', val)} />
                <Input type="number" placeholder="Max Qty" value={tier.max} onChange={(val) => updateTier(i, 'max', val)} />
                <Input type="number" placeholder="Discount %" value={tier.discount} onChange={(val) => updateTier(i, 'discount', val)} />
                <Button variant="danger" size="sm" onClick={() => removeTier(i)}>
                  <TrashIcon className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setModalOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button variant="primary" size="sm" onClick={handleAddItem} disabled={isSaving}>
              {isSaving ? 'Adding...' : 'Add Item'}
            </Button>
          </div>
        </div>
      </Modal>
    </PageContainer>
  );
}

export function FestiveMenuEditorPage() {
  return (
    <ErrorBoundary>
      <FestiveMenuEditorContent />
    </ErrorBoundary>
  );
}
